/**
 * `useRelativeTime` — React hook that returns a relative-time string
 * and re-renders on a fixed `intervalMs` cadence (default 10s).
 *
 * Replaces hand-rolled `setInterval` + `useState` ticker patterns in
 * `AutosaveStatus` and `DraftBadge` so every consumer gets:
 *   - consistent re-render cadence (configurable via `intervalMs`)
 *   - automatic teardown on unmount
 *   - reactive update when the input timestamp changes
 *
 * Note: `WorkflowDesignerWithCode` is intentionally NOT migrated. Its
 * "Updated Ns ago" message needs an adaptive 1s/1m cadence with
 * per-second precision in the first minute, which a single fixed
 * `intervalMs` cannot model. That site keeps its custom ticker.
 *
 * Accepts the same `RelativeTimeOptions` as `getRelativeTime` (style,
 * fallback) plus an `intervalMs` override for callers that need a
 * specific cadence.
 */

import { useEffect, useState } from 'react';
import { getRelativeTime, type RelativeTimeOptions } from './relativeTime';
import { toDate, type DateInput } from './types';

export interface UseRelativeTimeOptions extends RelativeTimeOptions {
  /**
   * Re-render cadence in milliseconds. Default `10_000` (10s) — matches
   * the legacy autosave/draft ticker.
   */
  intervalMs?: number;
}

export function useRelativeTime(
  value: DateInput,
  options: UseRelativeTimeOptions = {},
): string {
  const { intervalMs = 10_000, ...formatOptions } = options;

  // Re-render trigger. The hook is stateless beyond the tick counter —
  // formatting happens fresh every render off the latest input.
  const [, setTick] = useState(0);

  // Stable identity for the effect dependency. `Date` instances created
  // anew with the same timestamp would otherwise trigger the effect on
  // every render and cause an interval to be torn down + recreated.
  const date = toDate(value);
  const timestamp = date?.getTime() ?? null;

  useEffect(() => {
    if (timestamp === null) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [timestamp, intervalMs]);

  return getRelativeTime(value, formatOptions);
}
